// PostHogProvider.js
// Provides PostHog analytics and session replay across the entire app

import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import posthog from 'posthog-js';

// Initialize PostHog
// Replace 'YOUR_POSTHOG_API_KEY' with your actual PostHog API key
// You can get this from your PostHog project settings
const POSTHOG_API_KEY = process.env.REACT_APP_POSTHOG_API_KEY || 'phc_oyTvVs3l9ugQcqB4umBnyiG9RT1sFhKHgKs9UJfSAuc';
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

// Track if PostHog is initialized
let isPostHogInitialized = false;

// Only initialize PostHog if API key is provided
if (POSTHOG_API_KEY && POSTHOG_API_KEY !== 'YOUR_POSTHOG_API_KEY' && typeof window !== 'undefined') {
  posthog.init(POSTHOG_API_KEY, {
    api_host: POSTHOG_HOST,
    // Enable session replay
    session_recording: {
      maskAllInputs: true, // Mask sensitive input fields
      maskTextSelector: '[data-sensitive]', // Mask elements with data-sensitive attribute
      recordCrossOriginIframes: false,
    },
    // Capture pageviews automatically
    capture_pageview: false, // We'll handle this manually for better control
    capture_pageleave: true,
    // Enable autocapture for clicks, form submissions, etc.
    autocapture: true,
    // Load PostHog asynchronously
    loaded: (posthog) => {
      isPostHogInitialized = true;
      if (process.env.NODE_ENV === 'development') {
        console.log('PostHog loaded successfully');
      }
    },
  });
}

// Helper function to check if PostHog is ready
const isPostHogReady = () => {
  return isPostHogInitialized && typeof window !== 'undefined' && posthog.__loaded;
};

export const PostHogProvider = ({ children, user, userProfile }) => {
  const location = useLocation();
  const [isReady, setIsReady] = useState(false);

  // Wait for PostHog to be ready
  useEffect(() => {
    let retryCount = 0;
    const maxRetries = 50; // Stop after 5 seconds (50 * 100ms)
    
    const checkReady = () => {
      if (isPostHogReady()) {
        setIsReady(true);
      } else if (retryCount < maxRetries) {
        retryCount++;
        // Check again after a short delay if not ready yet
        setTimeout(checkReady, 100);
      } else {
        // PostHog didn't load, but continue anyway
        if (process.env.NODE_ENV === 'development') {
          console.warn('PostHog did not load within expected time');
        }
      }
    };
    
    const timeoutId = setTimeout(checkReady, 100);
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  // Identify user when they log in
  useEffect(() => {
    if (!isReady) return;

    if (user) {
      // Identify the user with their Firebase UID
      posthog.identify(user.uid, {
        email: user.email,
        name: userProfile?.name || user.displayName,
        onboardingComplete: userProfile?.onboardingComplete || false,
        familyId: userProfile?.familyId || null,
      });
    } else {
      // Reset when user logs out
      posthog.reset();
    }
  }, [user, userProfile, isReady]);

  // Track page views on route changes
  useEffect(() => {
    if (!isReady) return;

    posthog.capture('$pageview', {
      $current_url: window.location.href,
      path: location.pathname,
    });
  }, [location, isReady]);

  return <>{children}</>;
};

// Export a function to capture custom events
export const captureEvent = (eventName, properties = {}) => {
  if (isPostHogReady()) {
    posthog.capture(eventName, properties);
  }
};

// Export PostHog instance for direct access if needed
export { posthog };
